import { Alert, Button } from '@mui/material';
import { Component, ReactNode } from 'react';

import { Icon } from '@/components/Icon/Icon';

import { VITE_PROD } from '@/constants/configs';

import { getTranslatedMessage } from '@/i18n/utils';

/*
Currently, React doesn't have a direct equivalent to componentDidCatch or getDerivedStateFromError for function components so we still use Class Component here and wait for the upcoming React RFC about error boundaries in function components to be implemented.
*/

type Props = {
  children: ReactNode;
  fallback?: (error: Error | null) => ReactNode;
};

type State = {
  hasError: boolean;
  isChunkError: boolean;
  error: Error | null;
};

type ErrorInfo = {
  componentStack: string;
};

const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /import(?:ing)? a module script failed/i,
  /failed to load module script/i,
  /unable to preload css/i,
];

export function isChunkLoadError(error: Error | null): boolean {
  if (!error) {
    return false;
  }

  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(error.message));
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isChunkError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      console.log('Error caught by boundary:', error);
      console.log('Component stack:', errorInfo.componentStack);
    } else {
      console.error('Error caught by boundary:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.state.isChunkError && VITE_PROD) {
        return (
          <Alert
            icon={<Icon name="alert" />}
            color="info"
            variant="filled"
            action={
              <Button
                color="primary"
                variant="outlined"
                size="small"
                onClick={() => globalThis.location.reload()}
              >
                {getTranslatedMessage(
                  'common.errorBoundary.chunkError.reloadButton',
                )}
              </Button>
            }
          >
            {getTranslatedMessage('common.errorBoundary.chunkError.message')}
          </Alert>
        );
      }

      if (this.props.fallback) {
        return this.props.fallback(this.state.error);
      }

      return (
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          {this.state.error?.message ||
            getTranslatedMessage('common.errorBoundary.defaultError')}
        </Alert>
      );
    }

    return this.props.children;
  }
}
